/**
 * Remark Plugin: Inline Citations
 *
 * Converts `[N]` patterns in markdown text into custom `citation` AST nodes.
 * These are rendered as interactive CitationButton components by react-markdown.
 *
 * Rules:
 * - Converts [N] where N is 1+ digits
 * - Handles malformed LLM output: [1, 3] → [1][3], [L1 EMP] → [L1]
 * - Skips patterns inside code blocks and inline code
 * - Only converts when N maps to a valid source index (1-based)
 */

import { visit } from 'unist-util-visit'
import type { Plugin } from 'unified'
import type { Root, Text, Parent, PhrasingContent } from 'mdast'

// Individual citation: [1], [2], [10]
const CITATION_REGEX = /\[(\d+)\]/g

// Comma-separated citations: [1, 3] or [1,3] or [1, 3, 7]
const COMBINED_CITATION_REGEX = /\[(\d+(?:\s*,\s*\d+)+)\]/g

// Individual library citation: [L1], [L2], [L10]
const LIBRARY_CITATION_REGEX = /\[L(\d+)\]/g

// Library citation with trailing text: [L1 EMP] or [L1 Environmental Management Plan]
const ANNOTATED_LIBRARY_CITATION_REGEX = /\[L(\d+)\s+[^\]]+\]/g

// Tender citation with trailing text: [1 EMP] or [1 Document Name]
const ANNOTATED_CITATION_REGEX = /\[(\d+)\s+[^\]]+\]/g

/**
 * Mirror of `CITATION_LABEL_CEILING` in `backend/src/lib/citation-quotes.ts`.
 *
 * A marker at or below the ceiling is treated as a citation attempt, and is
 * dropped when it resolves to nothing. Above it, `[14001]` is ordinary prose —
 * ISO numbers, clause numbers, standards — and must survive untouched. The two
 * copies are a deliberate duplication (the backend module is not importable
 * here); if you change one, change both.
 */
export const CITATION_LABEL_CEILING = 50

/**
 * Insert a space between a bare URL and a citation marker glued to it.
 *
 * WHY: with `remark-gfm` enabled (every render site does), the autolink-literal
 * extension consumes a following `[N]` INTO the link URL when no whitespace
 * separates them — `(https://x/)[10]` parses as
 * `text:"("` + `link:"https://x/)[10"` + `text:"]"`. The marker is split across
 * three AST nodes, so CITATION_REGEX can never match it and it renders as raw
 * visible text (E2E 2026-08-09 finding #2). The collision is index-independent:
 * a perfectly valid `[1]` fails the same way.
 *
 * This MUST run on the raw markdown string BEFORE ReactMarkdown parses it — a
 * remark plugin runs after the parse that does the damage, which is too late.
 *
 * The variable-length lookbehind is deliberate and required: the obvious
 * alternative (matching URL and marker together with a lazy `\S*?`) cannot
 * separate consecutive markers, because after consuming `[1]` the next match
 * would need another URL scheme and `[2]` stays glued. It fires only when the
 * marker is preceded by an unbroken run of non-whitespace starting at a URL
 * scheme or `www.` — exactly the condition under which the autolink swallows it.
 *
 * The trailing lookahead keeps it off bracketed digits that are PART of a URL
 * rather than glued after one: `…/search?f[0]=b` must survive intact, so a
 * marker is only separated when what follows ends the token — whitespace, end
 * of input, sentence punctuation, or another `[` (consecutive markers).
 *
 * A `[N]` glued directly after a real markdown link's closing paren
 * (`[text](url)[1]`) IS separated; that is harmless and renders identically.
 *
 * KNOWN LIMITATION, accepted: running pre-parse means this cannot skip code
 * spans or fenced blocks the way every plugin in this file does, so a URL
 * followed by `[1]` inside a code sample gains a space. Fixing that would
 * require parsing first, which is precisely what breaks the marker.
 */
export function separateUrlAdjacentCitations(markdown: string): string {
  return markdown.replace(
    /(?<=(?:https?:\/\/|www\.)\S*)\[(L?\d+)\](?=$|[\s.,;:!?)\]]|\[)/g,
    ' [$1]'
  )
}

/**
 * Move a run of well-formed citation markers that LEADS a markdown list item
 * to the end of that item's line, so citations trail the claim they support.
 *
 * The model was observed front-loading markers on bullets ("[L1][L2] Case
 * Study 14: …"). A prompt rule now forbids it, but stored messages re-render
 * from the DB forever — this transform retro-fixes them at render time.
 *
 * Scope is deliberately narrow — a line matching: optional indent, a list
 * prefix (`-`, `*`, `+`, or `N.`/`N)`), a run of one or more well-formed
 * `[N]`/`[LN]` markers (optionally space-separated), then real text. Anything
 * else — prose lines, mid-bullet markers, marker-only bullets, malformed
 * forms like `[1, 3]` or `[L1 EMP]` (owned by the existing normalizers) — is
 * untouched.
 *
 * Same pre-parse limitation as separateUrlAdjacentCitations, accepted: it
 * cannot skip fenced code blocks. The list-prefix requirement makes a false
 * hit inside a code sample vanishingly unlikely.
 *
 * MUST be applied to the string fed to parseUsedCitations AND the string fed
 * to ReactMarkdown (use normalizeCitationMarkdown for both). Moving a marker
 * changes first-appearance order, and display numbers are assigned by first
 * appearance — normalizing one side but not the other makes badge numbers
 * disagree with the rail.
 */
// The (?!\[L?\d+\]) guard blocks greedy backtracking from splitting a
// marker-only bullet: without it, "- [L1][L2]" matches with run=[L1] and
// rest=[L2] and gets rewritten. With it, a run followed by another marker
// can never end there, so a bullet that is ONLY markers fails to match.
const LEADING_LIST_CITATION_RUN =
  /^([ \t]*(?:[-*+]|\d{1,3}[.)])[ \t]+)((?:\[L?\d+\][ \t]*)+)(?!\[L?\d+\])(?=\S)(.*)$/gm

export function moveLeadingListItemCitations(markdown: string): string {
  return markdown.replace(
    LEADING_LIST_CITATION_RUN,
    (_match, prefix: string, run: string, rest: string) => {
      const markers = (run.match(/\[L?\d+\]/g) ?? []).join('')
      return `${prefix}${rest.trimEnd()} ${markers}`
    }
  )
}

/**
 * Close the whitespace that strands a citation chip from what it belongs to.
 *
 * Chips render as raised pills with their own horizontal padding, so a literal
 * space either side reads as a layout bug rather than a space: `stabilization
 * [L1] .` puts the full stop adrift from its sentence, and `[L1] [L2]` looks
 * like two unrelated chips. Both forms come out of the model.
 *
 * Two surgical rules, deliberately NOT a general "space before a marker" tidy:
 * the space between a word and the marker that follows it is correct and stays.
 * Collapsing that one would also weld a list prefix to a marker-only bullet
 * (`- [L1] [L2]` → `-[L1][L2]`), destroying the list.
 *
 * Line-interior only — `[ \t]` excludes newlines, so a marker ending one line
 * can never absorb punctuation opening the next.
 *
 * MUST run BEFORE separateUrlAdjacentCitations, which deliberately INSERTS a
 * space in front of a marker glued to a URL. Running it after would undo that.
 */
export function tightenCitationSpacing(markdown: string): string {
  return markdown
    // "[L1] [L2]" → "[L1][L2]" (lookahead, so runs of 3+ collapse in one pass)
    .replace(/(\[L?\d+\])[ \t]+(?=\[L?\d+\])/g, '$1')
    // "[L1] ." → "[L1]."
    .replace(/(\[L?\d+\])[ \t]+(?=[.,;:!?])/g, '$1')
}

/**
 * The single normalization every citation-bearing markdown surface applies
 * before BOTH parsing used citations and rendering. Composes the leading-run
 * mover, the spacing tidy, and URL/citation separation. Call sites must not
 * call the parts directly — see the ordering warnings on
 * moveLeadingListItemCitations and tightenCitationSpacing.
 */
export function normalizeCitationMarkdown(markdown: string): string {
  return separateUrlAdjacentCitations(
    tightenCitationSpacing(moveLeadingListItemCitations(markdown))
  )
}

// Attachment attribution token — spec 19: `[ATT:` + same-line payload + `]`.
//
// A deliberate literal MIRROR of ATT_REGEX in `@/lib/annotations`, not an
// import: that module is a plain string scanner, whereas this one has to drive
// its own /g cursor while building remark AST nodes. The grammar must stay
// identical — newline-bounded on both the prefix gap and the payload, so an
// unclosed token fails visible rather than running to the next `]` and eating
// real prose. `remark-citations.test.ts` pins the two copies together; if you
// change one, change both.
const ATT_ATTRIBUTION_REGEX = /\[ATT:[^\S\n]*([^\]\n]+)\]/g

export interface CitationNode {
  type: 'citation'
  data: {
    hName: 'citation'
    hProperties: {
      index: number
    }
  }
  value: string
}

export interface AttAttributionNode {
  type: 'att-attribution'
  data: {
    hName: 'att-attribution'
    hProperties: {
      filename: string
    }
  }
  value: string
}

export interface LibraryCitationNode {
  type: 'library-citation'
  data: {
    hName: 'library-citation'
    hProperties: {
      index: number
    }
  }
  value: string
}

/**
 * Normalize malformed citation patterns in text before AST processing.
 * - [1, 3] → [1][3]
 * - [L1, L3] → [L1][L3]
 * - [L1 EMP] → [L1]
 * - [1 Document Name] → [1]
 */
/**
 * Bound for the annotated rewrites, naming which family the caller owns.
 *
 * Collapsing `[3 May 2026]` to `[3]` is only ever desirable when the result can
 * actually BECOME a citation. Out of range it is pure damage: the marker is not
 * converted, and anything that later removes it takes "May 2026" with it — a
 * tender deadline silently vanishing from the answer. So the rewrite is applied
 * only within the caller's source count, and only to the caller's own family
 * (a tender plugin must not collapse `[L3 EMP]` past the library plugin's
 * bound, and vice versa). Omit the argument for the unbounded, both-family
 * behaviour the strip and streaming-placeholder passes need.
 */
type NormalizeBounds = { tender: number } | { library: number }

function normalizeCitations(text: string, bounds?: NormalizeBounds): string {
  let result = text

  const libraryBound = !bounds ? Infinity : 'library' in bounds ? bounds.library : -1
  const tenderBound = !bounds ? Infinity : 'tender' in bounds ? bounds.tender : -1

  // [L1 EMP] or [L1 Environmental Management Plan] → [L1]
  // The `>= 1` lower edge matters as much as the upper one: `[L0 to 5]` is a
  // range in prose, never a citation, and collapsing it to `[L0]` loses "to 5"
  // the moment anything else in the node causes a rebuild.
  result = result.replace(ANNOTATED_LIBRARY_CITATION_REGEX, (match, num: string) =>
    Number(num) >= 1 && Number(num) <= libraryBound ? `[L${num}]` : match
  )

  // [1 EMP] or [1 Document Name] → [1]
  result = result.replace(ANNOTATED_CITATION_REGEX, (match, num: string) =>
    Number(num) >= 1 && Number(num) <= tenderBound ? `[${num}]` : match
  )

  // [L1, L3] or [L1,L3] or [L1, L3, L7] → [L1][L3] or [L1][L3][L7]
  // Also handles mixed: [L1, 3] → [L1][L3] (treat all as library in L-prefixed bracket)
  result = result.replace(
    /\[(L\d+(?:\s*,\s*L?\d+)+)\]/g,
    (_match, inner: string) => {
      return inner
        .split(/\s*,\s*/)
        .map((part) => {
          const num = part.replace(/^L/, '')
          return `[L${num}]`
        })
        .join('')
    }
  )

  // [1, 3] or [1,3] or [1, 3, 7] → [1][3] or [1][3][7]
  result = result.replace(COMBINED_CITATION_REGEX, (_match, inner: string) => {
    return inner
      .split(/\s*,\s*/)
      .map((num) => `[${num.trim()}]`)
      .join('')
  })

  return result
}

/**
 * Create a remark plugin that converts `[ATT: filename]` into
 * `att-attribution` nodes, rendered as a quiet attribution chip.
 *
 * Register this BEFORE any citation plugin. Two reasons:
 * 1. The citation plugins run `normalizeCitations` over the raw text, which
 *    rewrites bracket shapes. Lifting the token out first means no future
 *    normalizer can mistake `[ATT: …]` for a malformed citation.
 * 2. `remarkStripCitations` rewrites text-node values in place; a token already
 *    promoted to its own node is out of that pass's reach.
 *
 * The payload is NOT normalized or parsed. The drafter prompt binds the
 * filename, not the token payload, so the model may append a qualifier
 * (`[ATT: site-notes.pdf, p.4]`). Whatever sits between the prefix and the
 * closing bracket is the label, trimmed and rendered verbatim.
 */
export function remarkAttAttributions(): Plugin<[], Root> {
  return () => {
    return (tree) => {
      visit(tree, 'text', (node: Text, index: number | undefined, parent: Parent | undefined) => {
        if (!parent || index === undefined) return

        // Skip if inside code or inlineCode
        if (parent.type === 'code' || parent.type === 'inlineCode') return

        const value = node.value
        ATT_ATTRIBUTION_REGEX.lastIndex = 0
        if (!ATT_ATTRIBUTION_REGEX.test(value)) return
        ATT_ATTRIBUTION_REGEX.lastIndex = 0

        const children: PhrasingContent[] = []
        let lastIndex = 0
        let match: RegExpExecArray | null

        while ((match = ATT_ATTRIBUTION_REGEX.exec(value)) !== null) {
          // `[ATT:   ]` (whitespace-only payload) still matches, because the
          // canonical scanner matches it and strips it from exports. Leaving it
          // as text here would leak the raw token into the one surface spec 19
          // exists to clean. The chip falls back to a generic label instead.
          const filename = match[1]!.trim()

          if (match.index > lastIndex) {
            children.push({
              type: 'text',
              value: value.slice(lastIndex, match.index),
            })
          }

          // Attribution node — cast needed since 'att-attribution' is a custom AST type
          children.push({
            type: 'att-attribution',
            data: {
              hName: 'att-attribution',
              hProperties: {
                filename,
              },
            },
            value: match[0],
          } as unknown as PhrasingContent)

          lastIndex = match.index + match[0].length
        }

        if (children.length === 0) return

        if (lastIndex < value.length) {
          children.push({
            type: 'text',
            value: value.slice(lastIndex),
          })
        }

        (parent.children as PhrasingContent[]).splice(index, 1, ...children)
      })
    }
  }
}

/**
 * Create a remark plugin that converts [N] into citation nodes.
 *
 * @param sourceCount - Number of available sources (citations with index > sourceCount are left as text)
 */
export function remarkCitations(sourceCount: number): Plugin<[], Root> {
  return () => {
    return (tree) => {
      visit(tree, 'text', (node: Text, index: number | undefined, parent: Parent | undefined) => {
        if (!parent || index === undefined) return

        // Skip if inside code or inlineCode
        if (parent.type === 'code' || parent.type === 'inlineCode') return

        // Normalize malformed citations before processing. Bounded to this
        // plugin's own family and source count so an out-of-range annotated
        // bracket like `[3 May 2026]` is never collapsed into a droppable `[3]`.
        const value = normalizeCitations(node.value, { tender: sourceCount })
        if (!CITATION_REGEX.test(value)) return

        // Reset regex
        CITATION_REGEX.lastIndex = 0

        const children: PhrasingContent[] = []
        let lastIndex = 0
        let match: RegExpExecArray | null

        let dropped = false

        while ((match = CITATION_REGEX.exec(value)) !== null) {
          const citationIndex = parseInt(match[1]!, 10)

          // Only convert if index is within source bounds (1-based)
          if (citationIndex < 1 || citationIndex > sourceCount) {
            // An orphan citation-shaped marker points at nothing the reader can
            // open, so leaving it visible is model bookkeeping leaking into the
            // answer (E2E 2026-08-09 #2). Drop it — but only inside the ceiling,
            // so ISO-style numbers like [14001] survive as ordinary prose, and
            // only at/above 1, so [0] is left alone as plain text.
            //
            // Safe to drop on the normalized value: normalizeCitations is bounded
            // by sourceCount above, so an out-of-range annotated bracket such as
            // `[3 May 2026]` is never collapsed into a `[3]` this loop could see.
            // Guarding here instead (e.g. "only drop what the author wrote") was
            // tried and is unsound — it lets `See [3] and [3 May 2026]` delete the
            // date, and it blocks comma-combined orphans like `[1, 3]` from ever
            // being dropped.
            if (citationIndex >= 1 && citationIndex <= CITATION_LABEL_CEILING) {
              // Consume one preceding space/tab, mirroring STRIP_CITATION_REGEX,
              // so removing "clause [10]." leaves "clause." not "clause .".
              let before = value.slice(lastIndex, match.index)
              if (/[ \t]$/.test(before)) before = before.slice(0, -1)
              if (before) {
                children.push({ type: 'text', value: before })
              }
              lastIndex = match.index + match[0].length
              dropped = true
            }
            continue
          }

          // Text before citation
          if (match.index > lastIndex) {
            children.push({
              type: 'text',
              value: value.slice(lastIndex, match.index),
            })
          }

          // Citation node — cast needed since 'citation' is a custom AST type
          children.push({
            type: 'citation',
            data: {
              hName: 'citation',
              hProperties: {
                index: citationIndex,
              },
            },
            value: match[0],
          } as unknown as PhrasingContent)

          lastIndex = match.index + match[0].length
        }

        // Nothing converted AND nothing removed — leave the node untouched.
        // `dropped` is load-bearing: a node whose ONLY marker was dropped at
        // index 0 pushes no preceding text, so `children` is still empty here
        // and a length-only check would return with the marker intact.
        if (children.length === 0 && !dropped) return

        // Remaining text after last citation
        if (lastIndex < value.length) {
          children.push({
            type: 'text',
            value: value.slice(lastIndex),
          })
        }

        // Replace the text node with the new children
        (parent.children as PhrasingContent[]).splice(index, 1, ...children)
      })
    }
  }
}

// Any citation marker, tender or library, while a turn is still streaming.
const PENDING_CITATION_REGEX = /\[L?(\d+)\]/g

export interface PendingCitationNode {
  type: 'pending-citation'
  data: {
    hName: 'pending-citation'
    hProperties: Record<string, never>
  }
  value: string
}

/**
 * Create a remark plugin that converts every `[N]` / `[LN]` marker into a
 * neutral `pending-citation` node.
 *
 * Used only while a turn is streaming. The model's raw indices are not the
 * numbers the finished answer will show — sources arrive at turn completion
 * and are renumbered in order of first use — so rendering the raw number mid
 * stream shows a figure that silently changes at the end. This plugin
 * deliberately carries no index: there is nothing honest to display yet, so
 * the marker renders as a placeholder and resolves to the real, clickable
 * citation when the sources land.
 */
export function remarkPendingCitations(): Plugin<[], Root> {
  return () => {
    return (tree) => {
      visit(tree, 'text', (node: Text, index: number | undefined, parent: Parent | undefined) => {
        if (!parent || index === undefined) return
        if (parent.type === 'code' || parent.type === 'inlineCode') return

        const value = normalizeCitations(node.value)
        PENDING_CITATION_REGEX.lastIndex = 0
        if (!PENDING_CITATION_REGEX.test(value)) return
        PENDING_CITATION_REGEX.lastIndex = 0

        const children: PhrasingContent[] = []
        let lastIndex = 0
        let match: RegExpExecArray | null

        while ((match = PENDING_CITATION_REGEX.exec(value)) !== null) {
          if (match.index > lastIndex) {
            children.push({ type: 'text', value: value.slice(lastIndex, match.index) })
          }

          children.push({
            type: 'pending-citation',
            data: {
              hName: 'pending-citation',
              hProperties: {},
            },
            value: match[0],
          } as unknown as PhrasingContent)

          lastIndex = match.index + match[0].length
        }

        if (children.length === 0) return

        if (lastIndex < value.length) {
          children.push({ type: 'text', value: value.slice(lastIndex) })
        }

        (parent.children as PhrasingContent[]).splice(index, 1, ...children)
      })
    }
  }
}

// A marker plus the single space in front of it, so removing "July [1]." leaves
// "July." rather than "July ." .
const STRIP_CITATION_REGEX = /[ \t]?\[L?\d+\]/g

/**
 * Create a remark plugin that REMOVES every citation marker.
 *
 * For a finished turn that ended up with no sources at all — the shape where
 * every citation was discarded server-side — there is nothing for a marker to
 * point at and nothing still coming. Leaving the raw `[N]` exposes the model's
 * bookkeeping as body text, and a "pending" placeholder would be a lie because
 * the turn is over. So the markers collapse and the prose reads normally.
 */
export function remarkStripCitations(): Plugin<[], Root> {
  return () => {
    return (tree) => {
      visit(tree, 'text', (node: Text, _index: number | undefined, parent: Parent | undefined) => {
        if (!parent) return
        if (parent.type === 'code' || parent.type === 'inlineCode') return

        const stripped = normalizeCitations(node.value).replace(STRIP_CITATION_REGEX, '')
        if (stripped !== node.value) node.value = stripped
      })
    }
  }
}

/**
 * Create a remark plugin that converts [LN] into library-citation nodes.
 *
 * @param librarySourceCount - Number of available library sources (citations with index > count are left as text)
 */
export function remarkLibraryCitations(librarySourceCount: number): Plugin<[], Root> {
  return () => {
    return (tree) => {
      visit(tree, 'text', (node: Text, index: number | undefined, parent: Parent | undefined) => {
        if (!parent || index === undefined) return

        // Skip if inside code or inlineCode
        if (parent.type === 'code' || parent.type === 'inlineCode') return

        // Normalize malformed citations before processing. Bounded — see the
        // tender-family note above.
        const value = normalizeCitations(node.value, { library: librarySourceCount })
        if (!LIBRARY_CITATION_REGEX.test(value)) return

        // Reset regex
        LIBRARY_CITATION_REGEX.lastIndex = 0

        const children: PhrasingContent[] = []
        let lastIndex = 0
        let match: RegExpExecArray | null

        let dropped = false

        while ((match = LIBRARY_CITATION_REGEX.exec(value)) !== null) {
          const citationIndex = parseInt(match[1]!, 10)

          // Only convert if index is within source bounds (1-based)
          if (citationIndex < 1 || citationIndex > librarySourceCount) {
            // Same rule as the tender family above — bounded normalisation keeps
            // `[L3 EMP]` intact, and one space is consumed to avoid "plan ."
            // residue.
            if (citationIndex >= 1 && citationIndex <= CITATION_LABEL_CEILING) {
              let before = value.slice(lastIndex, match.index)
              if (/[ \t]$/.test(before)) before = before.slice(0, -1)
              if (before) {
                children.push({ type: 'text', value: before })
              }
              lastIndex = match.index + match[0].length
              dropped = true
            }
            continue
          }

          // Text before citation
          if (match.index > lastIndex) {
            children.push({
              type: 'text',
              value: value.slice(lastIndex, match.index),
            })
          }

          // Library citation node
          children.push({
            type: 'library-citation',
            data: {
              hName: 'library-citation',
              hProperties: {
                index: citationIndex,
              },
            },
            value: match[0],
          } as unknown as PhrasingContent)

          lastIndex = match.index + match[0].length
        }

        // See the tender-family note: `dropped` covers the empty-children case
        // where the only marker sat at index 0.
        if (children.length === 0 && !dropped) return

        // Remaining text after last citation
        if (lastIndex < value.length) {
          children.push({
            type: 'text',
            value: value.slice(lastIndex),
          })
        }

        // Replace the text node with the new children
        (parent.children as PhrasingContent[]).splice(index, 1, ...children)
      })
    }
  }
}

// ===================
// String-level marker rewrites (clipboard)
// ===================
//
// The render path works on the remark AST. Copy works on the Markdown string,
// so these two mirror the render rules at string level for the text a user
// copies to match what they see. Same pre-parse limitation as
// normalizeCitationMarkdown: a marker inside a code sample is rewritten too.

/**
 * Remove every citation marker, and the single space in front of it — the
 * string twin of `remarkStripCitations`. Expects raw Markdown; normalizes it
 * first so "claim [1] [2]." comes out as "claim." with no stray space.
 */
export function stripCitationMarkers(markdown: string): string {
  return normalizeCitations(normalizeCitationMarkdown(markdown)).replace(STRIP_CITATION_REGEX, '')
}

/** One marker family's render state: how many sources it has, and its display numbering. */
export interface CitationFamilyNumbering {
  sourceCount: number
  /** Original label index → display index, in order of first use. */
  origToDisplay: Map<number, number>
}

/**
 * Rewrite each marker to the number the reader sees — the string twin of the
 * `remarkCitations` / `remarkLibraryCitations` render. `[L12]…[L3]` becomes
 * `[L1]…[L2]` when those are the first two library labels used.
 *
 * Pass `null` for a family the render leaves alone (no sources in it while the
 * other family has some): its markers survive verbatim, exactly as on screen.
 * Within a rendered family, a marker with no source is dropped with its space
 * when it is at or under the citation ceiling, and kept above it (prose like
 * "ISO [14001]"). Expects the string the mapping was computed from — the output
 * of normalizeCitationMarkdown.
 */
export function renumberCitationMarkers(
  normalizedMarkdown: string,
  tender: CitationFamilyNumbering | null,
  library: CitationFamilyNumbering | null
): string {
  let out = normalizedMarkdown
  if (tender) out = normalizeCitations(out, { tender: tender.sourceCount })
  if (library) out = normalizeCitations(out, { library: library.sourceCount })

  return out.replace(/([ \t]?)\[(L?)(\d+)\]/g, (match, space: string, prefix: string, digits: string) => {
    const family = prefix ? library : tender
    if (!family) return match
    const index = Number(digits)
    if (index >= 1 && index <= family.sourceCount) {
      const display = family.origToDisplay.get(index)
      return display === undefined ? match : `${space}[${prefix}${display}]`
    }
    if (index >= 1 && index <= CITATION_LABEL_CEILING) return ''
    return match
  })
}
